import { FC, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  FormatListBulleted as ListIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

import {
  ReconciliationFormData,
  ReconciliationStep,
  ReconciliationInput,
  ReconciliationPreviewResponse,
} from "@/types/reconciliation";
import {
  usePreviewReconciliationMutation,
  useProcessReconciliationMutation,
  useLazyGetDownloadStatusQuery,
  useLazyGetReportDownloadUrlQuery,
  useGetAllReportsQuery,
} from "@/services";
import { useEffectiveClientScope, useSnackbar } from "@/hooks";
import { tokens } from "@/tokens";

import StepConfigure from "./step-configure";
import StepPreview from "./step-preview";
import StepProgress from "./step-progress";

// ─── Step Breadcrumb ────────────────────────────────────────────
interface BreadcrumbStep {
  label: string;
  icon: React.ReactNode;
  status: "pending" | "active" | "complete";
}

const StepBreadcrumb: FC<{ steps: BreadcrumbStep[] }> = ({ steps }) => (
  <Stack direction="row" alignItems="center" spacing={0}>
    {steps.map((step, i) => {
      const isActive = step.status === "active";
      const isComplete = step.status === "complete";
      return (
        <Stack key={i} direction="row" alignItems="center" spacing={0}>
          {i > 0 && (
            <Box
              sx={{
                width: 48,
                height: 0,
                borderTop: `2px dashed ${
                  isComplete || isActive ? tokens.text.muted : tokens.border.default
                }`,
                mx: 0.5,
                transition: "border-color 0.3s ease",
              }}
            />
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: tokens.radius.full,
              border: `1.5px solid ${
                isActive
                  ? tokens.brand.primary
                  : isComplete
                  ? tokens.text.muted
                  : tokens.border.default
              }`,
              bgcolor: isActive ? tokens.brand.primary : "transparent",
              transition: "all 0.3s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: isActive ? "#fff" : isComplete ? tokens.text.secondary : tokens.text.muted,
                fontSize: 16,
              }}
            >
              {isComplete ? <CheckIcon sx={{ fontSize: 16 }} /> : step.icon}
            </Box>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                fontWeight: isActive ? tokens.fontWeight.semibold : tokens.fontWeight.normal,
                color: isActive ? "#fff" : isComplete ? tokens.text.secondary : tokens.text.muted,
                whiteSpace: "nowrap",
              }}
            >
              {step.label}
            </Typography>
          </Stack>
        </Stack>
      );
    })}
  </Stack>
);

// ─── Main Component ──────────────────────────────────────────────
const CreateAdjustment: FC = () => {
  const navigate = useNavigate();
  const { show: showSnackbar } = useSnackbar();
  const {
    clientId: effectiveClientId,
    tenantId,
    isTDAdmin,
    shouldRequireClientSelection: shouldSkip,
  } = useEffectiveClientScope();

  // Step state
  const [currentStep, setCurrentStep] = useState<ReconciliationStep>("configure");
  const [formData, setFormData] = useState<ReconciliationFormData | null>(null);
  const [previewData, setPreviewData] = useState<ReconciliationPreviewResponse | null>(null);

  // Processing state
  const [fileId, setFileId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<"PENDING" | "SUCCESS" | "FAILED">("PENDING");
  const [processingError, setProcessingError] = useState<string | null>(null);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Section refs for auto-scroll
  const reviewRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);

  // API hooks
  const [previewReconciliation, { isLoading: isPreviewLoading, error: previewError }] =
    usePreviewReconciliationMutation();
  const [processReconciliation, { isLoading: isProcessing }] =
    useProcessReconciliationMutation();
  const [getDownloadStatus] = useLazyGetDownloadStatusQuery();
  const [getReportDownloadUrl] = useLazyGetReportDownloadUrlQuery();

  const { data: reportsData, refetch } = useGetAllReportsQuery(
    {
      pageSize: 50,
      clientId: effectiveClientId,
      ...(isTDAdmin && tenantId && { tenantId }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  // Transform form data to API input format
  const transformToApiInput = useCallback(
    (data: ReconciliationFormData): ReconciliationInput => {
      if (!effectiveClientId) {
        throw new Error("No client selected");
      }

      const hasFilters =
        data.filters.reconFlag ||
        (data.filters.htsCodes && data.filters.htsCodes.length > 0) ||
        (data.filters.originCodes && data.filters.originCodes.length > 0) ||
        (data.filters.manufacturerIds && data.filters.manufacturerIds.length > 0) ||
        (data.filters.entryLines && data.filters.entryLines.length > 0);

      return {
        date_range: {
          start: data.dateRange.start,
          end: data.dateRange.end,
        },
        filters: hasFilters
          ? {
              ...(data.filters.reconFlag && { ReconFlag: data.filters.reconFlag }),
              ...(data.filters.htsCodes?.length && { hts_codes: data.filters.htsCodes }),
              ...(data.filters.originCodes?.length && { origin_codes: data.filters.originCodes }),
              ...(data.filters.manufacturerIds?.length && {
                manufacturer_ids: data.filters.manufacturerIds,
              }),
              ...(data.filters.entryLines?.length && { entry_lines: data.filters.entryLines }),
            }
          : undefined,
        adjustment: {
          type: data.adjustmentType,
          data: data.adjustmentData,
        },
        clientId: effectiveClientId,
        ...(isTDAdmin && tenantId && { tenantId }),
      };
    },
    [effectiveClientId, isTDAdmin, tenantId]
  );

  // Handle configuration submission
  const handleConfigSubmit = async (data: ReconciliationFormData) => {
    try {
      setFormData(data);
      const apiInput = transformToApiInput(data);
      const result = await previewReconciliation(apiInput).unwrap();
      setPreviewData(result);
      setCurrentStep("preview");
      // Auto-scroll to review section
      setTimeout(() => {
        reviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (error: any) {
      console.error("Preview failed:", error);
      showSnackbar("Error", "Failed to generate preview. Please check your configuration.", "error");
    }
  };

  // Handle preview confirmation
  const handlePreviewConfirm = async () => {
    if (!formData) return;

    try {
      const apiInput = transformToApiInput(formData);
      const result = await processReconciliation(apiInput).unwrap();

      setFileId(result.fileId);
      setCurrentStep("processing");

      // Auto-scroll to process section
      setTimeout(() => {
        processRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);

      if (!result.fileId) {
        throw new Error("Invalid response: missing fileId");
      }

      if (result.progressBar && result.fileId) {
        startPolling(result.fileId);
      } else if (result.signedUrl) {
        setDownloadUrl(result.signedUrl);
        setProcessingStatus("SUCCESS");
        setCurrentStep("complete");
      } else {
        console.warn("Unexpected response format: no progressBar or signedUrl", result);
        startPolling(result.fileId);
      }
    } catch (error: any) {
      console.error("Processing failed:", error);
      setProcessingError(error?.data?.message || "Processing failed. Please try again.");
      setProcessingStatus("FAILED");
    }
  };

  // Helper function to find report ID by fileId
  const findReportIdByFileId = useCallback(
    (targetFileId: string, data?: typeof reportsData): string | null => {
      const dataToSearch = data || reportsData;

      if (!dataToSearch?.data) {
        console.error(`No reports data available for fileId lookup: ${targetFileId}`);
        return null;
      }

      const matchingReport = dataToSearch.data.find(
        (report) => report.fileId === targetFileId
      );

      if (matchingReport) {
        return matchingReport._id;
      }

      console.error(`No report found with fileId: ${targetFileId}`);
      return null;
    },
    [reportsData]
  );

  // Start polling for download status
  const startPolling = async (fileId: string) => {
    try {
      const result = await getDownloadStatus(fileId).unwrap();

      if (result.status === "SUCCESS") {
        clearPollingInterval();

        const refetchResult = await refetch();
        const freshData = refetchResult.data;
        const reportId = findReportIdByFileId(fileId, freshData);

        if (reportId) {
          try {
            const downloadResult = await getReportDownloadUrl({ reportId }).unwrap();
            setDownloadUrl(downloadResult);
            setProcessingStatus("SUCCESS");
            setProgress(100);
            setCurrentStep("complete");
          } catch (urlError: any) {
            console.error("Failed to generate download URL:", urlError);
            setProcessingError("Failed to generate download URL. Please try again.");
            setProcessingStatus("FAILED");
          }
        } else {
          setProcessingError("Could not find report for download. Please try again.");
          setProcessingStatus("FAILED");
        }
      } else if (result.status === "FAILED") {
        clearPollingInterval();
        setProcessingError(result.msg || "Processing failed");
        setProcessingStatus("FAILED");
      } else {
        setProgress(result.progress || 0);
        setProcessingStatus("PENDING");
      }
    } catch (error: any) {
      console.error("Status check failed:", error);
      setProcessingError("Failed to check processing status");
      setProcessingStatus("FAILED");
    }
  };

  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Poll for status updates when file ID changes
  useEffect(() => {
    clearPollingInterval();

    if (fileId && processingStatus === "PENDING") {
      pollingIntervalRef.current = setInterval(() => {
        startPolling(fileId);
      }, 3000);
    }

    return clearPollingInterval;
  }, [fileId, processingStatus, clearPollingInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  // Handle download
  const handleDownload = async (url: string) => {
    try {
      window.open(url, "_blank");
      showSnackbar("Success", "Download started successfully!", "success");
    } catch (error) {
      console.error("Download failed:", error);
      showSnackbar("Error", "Failed to download file. Please try again.", "error");
    }
  };

  // Handle close / navigate back
  const handleClose = () => {
    clearPollingInterval();
    navigate("/compliance/post-import");
  };

  // Derived state
  const isProcessingOrComplete = currentStep === "processing" || currentStep === "complete";
  const showReview = currentStep !== "configure";
  const showProcess = isProcessingOrComplete;

  // Breadcrumb steps
  const breadcrumbSteps: BreadcrumbStep[] = useMemo(() => {
    const configStatus =
      currentStep === "configure" ? "active" : "complete";

    const reviewStatus =
      currentStep === "configure"
        ? "pending"
        : currentStep === "preview"
        ? "active"
        : "complete";

    let processStatus: BreadcrumbStep["status"] = "pending";
    if (currentStep === "processing") {
      processStatus = "active";
    } else if (currentStep === "complete") {
      processStatus = "complete";
    }

    return [
      { label: "Configure", icon: <SettingsIcon sx={{ fontSize: 16 }} />, status: configStatus },
      { label: "Review", icon: <VisibilityIcon sx={{ fontSize: 16 }} />, status: reviewStatus },
      { label: "Process", icon: <ListIcon sx={{ fontSize: 16 }} />, status: processStatus },
    ];
  }, [currentStep]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: tokens.bg.surface,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          bgcolor: tokens.bg.surface,
          borderBottom: `1px solid ${tokens.border.default}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton onClick={handleClose} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: tokens.text.secondary,
                  cursor: "pointer",
                  "&:hover": { color: tokens.brand.primary },
                }}
                onClick={handleClose}
              >
                Back to Post Import
              </Typography>
              <Typography variant="h6" fontWeight={tokens.fontWeight.bold}>
                Create Post-Import Adjustment
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.sm2 }}>
                  Configure parameters, preview impact, then process.
                </Typography>
                {previewData && (
                  <>
                    <Chip
                      icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                      label={`${previewData.recordCount.toLocaleString()} records`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: tokens.fontSize.xs, height: 22 }}
                    />
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                      label={`Est. ${previewData.estimatedProcessingTime}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: tokens.fontSize.xs, height: 22 }}
                    />
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
          <StepBreadcrumb steps={breadcrumbSteps} />
        </Stack>
      </Box>

      {/* Single-column content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: 2.5,
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Stack spacing={2}>
            {/* ── CONFIGURE ZONE ─────────────────────────── */}
            <Box
              sx={{
                ...(isProcessingOrComplete && {
                  opacity: 0.5,
                  pointerEvents: "none",
                }),
              }}
            >
              <StepConfigure
                onSubmit={handleConfigSubmit}
                isLoading={isPreviewLoading}
              />

              {/* Inline action: Preview Adjustment */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  type="submit"
                  form="config-form"
                  variant="contained"
                  disabled={isPreviewLoading || showReview}
                  endIcon={
                    isPreviewLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ArrowForwardIcon />
                    )
                  }
                  sx={{
                    bgcolor: tokens.brand.primary,
                    "&:hover": { bgcolor: tokens.brand.primaryHover },
                  }}
                >
                  {isPreviewLoading ? "Generating Preview..." : "Preview Adjustment"}
                </Button>
              </Box>
            </Box>

            {/* ── REVIEW ZONE ────────────────────────────── */}
            {showReview && (
              <Box
                ref={reviewRef}
                sx={{
                  borderTop: `1px solid ${tokens.border.default}`,
                  pt: 2,
                  ...(isProcessingOrComplete && {
                    opacity: 0.5,
                    pointerEvents: "none",
                  }),
                }}
              >
                {formData && (
                  <StepPreview
                    previewData={previewData || undefined}
                    formData={formData}
                    isLoading={isPreviewLoading}
                    isProcessing={isProcessing}
                    error={previewError ? "Failed to generate preview" : undefined}
                  />
                )}

                {/* Inline actions */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: tokens.brand.primary,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => {
                      setCurrentStep("configure");
                      setPreviewData(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    &larr; Edit Configuration
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={
                      isProcessing ||
                      !previewData ||
                      previewData.recordCount === 0
                    }
                    onClick={handlePreviewConfirm}
                    endIcon={
                      isProcessing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <ArrowForwardIcon />
                      )
                    }
                    sx={{
                      bgcolor: tokens.brand.primary,
                      "&:hover": { bgcolor: tokens.brand.primaryHover },
                    }}
                  >
                    {isProcessing ? "Processing..." : "Process Adjustment"}
                  </Button>
                </Stack>
              </Box>
            )}

            {/* ── PROCESSING ZONE ────────────────────────── */}
            {showProcess && (
              <Box
                ref={processRef}
                sx={{
                  borderTop: `1px solid ${tokens.border.default}`,
                  pt: 2,
                }}
              >
                <StepProgress
                  status={processingStatus}
                  progress={progress}
                  downloadUrl={downloadUrl || undefined}
                  error={processingError || undefined}
                  fileId={fileId || undefined}
                  adjustmentType={formData?.adjustmentType}
                  onDownload={handleDownload}
                  onClose={handleClose}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateAdjustment;
